import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useEvents } from "@/hooks/useEvents";
import { LeagueGroups } from "@/components/LeagueGroups";
import { SportIcon } from "@/components/SportIcon";
import { EventRow } from "@/components/EventRow";
import { PromoCarousel } from "@/components/PromoCarousel";
import { BoostedOdds, GamesStrip } from "@/components/HomeWidgets";

const ORDER = ["soccer", "rugby", "basketball", "american-football", "tennis", "ice-hockey", "mma", "aussie-rules", "baseball"];

function pickHighlights(evs) {
  const per = {};
  const out = [];
  evs.forEach((e) => {
    per[e.league] = (per[e.league] || 0) + 1;
    if (per[e.league] <= 4) out.push(e);
  });
  return out.sort((a, b) => ORDER.indexOf(a.sport) - ORDER.indexOf(b.sport)).slice(0, 70);
}

export default function Home() {
  const [q, setQ] = useState("");
  const { data: sports } = useQuery({ queryKey: ["sports"], queryFn: () => api.get("/sports").then((r) => r.data) });
  const { data: live } = useEvents({ state: "in" }, 20000);
  const { data: upcomingAll } = useEvents({ state: "pre", limit: 400, ...(q ? { q } : {}) });
  const upcoming = upcomingAll && (q ? upcomingAll.slice(0, 80) : pickHighlights(upcomingAll));
  return (
    <div className="space-y-6">
      <PromoCarousel liveCount={live?.length ?? 0} />
      <GamesStrip />
      <div className="scroll-thin flex gap-2 overflow-x-auto pb-1">
        {(sports || []).filter((s) => s.upcoming + s.live > 0).map((s) => (
          <Link
            key={s.key}
            to={`/sport/${s.key}`}
            data-testid={`home-sport-chip-${s.key}`}
            className="flex shrink-0 items-center gap-2 rounded-full border border-[#1E293B] bg-[#121824] px-4 py-2 text-sm transition-colors hover:border-[#10B981]"
          >
            <SportIcon sport={s.key} /> {s.name}
            <span className="text-xs text-slate-500">{s.upcoming + s.live}</span>
          </Link>
        ))}
      </div>
      <BoostedOdds events={upcomingAll} />
      {live?.length > 0 && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 font-display text-lg font-black uppercase tracking-tight">
            <span className="live-dot h-2 w-2 rounded-full bg-[#EF4444]" /> Live now
          </h2>
          <div className="overflow-hidden rounded-lg border border-[#EF4444]/30 bg-[#121824]">
            {live.slice(0, 6).map((e) => (
              <EventRow key={e.id} ev={e} />
            ))}
          </div>
        </section>
      )}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-black uppercase tracking-tight">Upcoming highlights</h2>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <Input
              data-testid="home-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search team or league"
              className="bg-[#121824] pl-9"
            />
          </div>
        </div>
        <LeagueGroups events={upcoming} empty="No upcoming matches found." />
      </section>
    </div>
  );
}
